// <div data-controller="components--editor-js" id="editorContainer">
//
// </div>
import { Controller } from "@hotwired/stimulus";
import api from "@/utils/api";
import EditorJS from '@editorjs/editorjs'; // import EditorJS from '/@fs/editor.js';
import Header from '@editorjs/header';
import Quote from "@editorjs/quote" // import Quote from "/@fs/quote"
import Delimiter from '@editorjs/delimiter';
import EditorjsList from '@editorjs/list';
import Table from '@editorjs/table'; // import Table from "/@fs/table/dist/table.mjs"; // 로컬 Table 라이브러리 경로
import Marker from '@editorjs/marker';
import Underline from '@editorjs/underline';
import DragDrop from 'editorjs-drag-drop';
import Paragraph from '@editorjs/paragraph';
import CodeTool from "@editorjs/code";
import Embed from "@editorjs/embed";
import GalleryTool from "@/utils/gallery_tool";
import TextColor from "editorjs-color";
import Button from "@/editorjs/tools/button/index";
import Strikethrough from "editorjs-strikethrough";
import Columns from '@/editorjs/tools/columns'
import SimpleImageTool from "@/editorjs/tools/simple-image/index";
import UnifiedUndo from "@/editorjs/tools/unified-undo/index";
import LinkTool from '@/editorjs/tools/link/index';

// Tunes
import BlockStyleTune from '@/editorjs/tunes/block-style/tune';
import TextStyleTune from '@/editorjs/tunes/text-style/tune';
import DuplicateTune from '@/editorjs/tunes/duplicate/tune';

import { createImageUploaderConfig } from '@/utils/image_uploader_config';

import korean from "@/i18n/editorjs-korean";


Delimiter.prototype.drawView = function () {
    let div = document.createElement('DIV');

    div.classList.add(this._CSS.wrapper, this._CSS.block);
    div.innerHTML = "<hr>";

    return div;
};

export default class extends Controller {
    static targets = [
        "editorForm", "editorData", "autoSaveSubmit", "autoSavedLabel", "saveAndPublish",
        "title", "subtitle", "tagList", "postCategorySlug", "warningLabel",
        "previewButton", "overlay", "actionsContainer"
    ];
    static values = {
        primaryBackgroundColor: String,
        primaryTextColor: String,
        lightMode: Boolean
    }

    initialize() {
        this.editor = null;
        this.undo = null;
        this.alreadySaving = false;
        this.isDragging = false;
        this.previewWindow = null;
        this.dragResetTimer = null; // 드래그 상태 리셋을 위한 타이머
        this.isTyping = false; // 타이핑 중인지 확인하는 플래그
        this.typingTimer = null; // 타이핑 종료 감지용 타이머
        this.isComposing = false; // IME 입력 중인지 확인하는 플래그

        // Safari 브라우저 감지
        this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        window.connectPreview = (previewWindowInstance) => {
            this.previewWindow = previewWindowInstance;

            // 연결되자마자 최신 내용으로 한번 새로고침 해줍니다.
            if (this.previewWindow && !this.previewWindow.closed) {
                this.previewWindow.refreshPreview();
            }
        };
    }

    submitForm() {
        clearInterval(this.autoSaver);

        if (this.isExceptionalEnvironment()) {
            return;
        }

        this.enableLeave();

        this.editor.save().then((outputData) => {
            this.editorDataTarget.value = JSON.stringify(outputData);
            this.editorFormTarget.submit();
        }).catch((error) => {
            alert(error.response.data.error);
        });
    }

    publishNote(e) {
        this.enableLeave();

        if (this.isExceptionalEnvironment()) {
            return;
        }

        this.editor.save().then((data) => {
            this.editorDataTarget.value = JSON.stringify(data);
            this.saveAndPublishTarget.click();
        });
    }

    enableLeave = () => {
        this.data.set("protect-leave", "off");
    };

    leavingPage = (event) => {
        const areYouSure = "정말 페이지를 나가시나요? 저장을 하셨는지 꼭 확인해주세요 :)";

        if (this.data.get("protect-leave") === "on") {
            if (event.type === "turbo:before-visit") {
                if (!window.confirm(areYouSure)) {
                    event.preventDefault()
                }
            } else {
                event.returnValue = areYouSure;
                return event.returnValue;
            }
        }
    };

    openPreview(event) {
        event.preventDefault();

        if (!this.editor || !this.editor.isReady) {
            alert("에디터가 아직 준비 중입니다. 잠시 후 다시 시도해주세요.");
            return;
        }

        // 팝업이 이미 열려 있고, 닫히지 않았다면 새로 열지 않고 포커스만 줍니다.
        if (this.previewWindow && !this.previewWindow.closed) {
            this.previewWindow.focus();
            return;
        }

        const {previewUrl} = event.target.dataset;

        // 가장 최근 내용이 반영되도록 먼저 강제 저장
        this.autoSave();

        // iPhone 14/15 Pro 크기에 맞춘 팝업 창 옵션 (테두리, 스크롤바 등 고려하여 약간 더 크게)
        const windowFeatures = "width=800,height=1060,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes";

        window.open(previewUrl, "preview", windowFeatures);
    }

    // deepl extension : chrome-extension://cofdbpoegempjloogbagkncekinflcnj/build/content.css
    isExceptionalEnvironment = () => {
        const deeplInlineTranslator = document.querySelectorAll("deepl-inline-translate");
        if (deeplInlineTranslator.length > 0) {
            alert("Deepl 익스텐션을 사용하시면 에디터가 정상적으로 작동하지 않습니다. Deepl 익스텐션을 완전 삭제 하신 후 사용해주세요.");
            return true;
        }

        return false;
    }

    sendCurrentBlockIdToPreview() {
        // 미리보기 창이 없거나 닫혔으면 아무것도 하지 않음
        if (!this.previewWindow || this.previewWindow.closed) {
            return;
        }

        try {
            // Editor.js API를 사용해 현재 블록의 인덱스를 가져옵니다.
            const currentBlockIndex = this.editor.blocks.getCurrentBlockIndex();
            // 인덱스를 사용해 실제 블록 객체를 가져옵니다.
            const currentBlock = this.editor.blocks.getBlockByIndex(currentBlockIndex);

            if (currentBlock) {
                const blockId = currentBlock.id;

                // 미리보기 창에 정의된 새로운 함수(scrollToBlock)를 호출합니다.
                if (typeof this.previewWindow.scrollToBlock === 'function') {
                    this.previewWindow.scrollToBlock(blockId);
                }
            }
        } catch (error) {
            console.error("블록 정보를 가져오는 데 실패했습니다:", error);
        }
    }

    disableEditor(warningMessage) {
        if (this.hasOverlayTarget) {
            this.overlayTarget.innerHTML = warningMessage;
            this.overlayTarget.style.display = 'block'; // 오버레이 표시
        }
    }

    enableEditor() {
        if (this.hasOverlayTarget) {
            this.overlayTarget.innerHTML = '';
            this.overlayTarget.style.display = 'none'; // 오버레이 숨김
        }
    }

    autoSave() {
        if (this.isDragging || this.alreadySaving || this.isTyping) {
            return;
        }

        if (this.isExceptionalEnvironment()) {
            return;
        }

        this.alreadySaving = true;
        this.editor.save().then((outputData) => {
            if (process.env.NODE_ENV === "development") {
                console.log(outputData.blocks)
            }

            // <--- 내용이 비어있고, 기존 post가 존재했다면 저장을 막는 로직 추가
            const isPostEmpty = !outputData.blocks || outputData.blocks.length === 0;
            const postExists = this.data.get("post-ext-id") !== "new";

            if (isPostEmpty && postExists) {
                // 기존에 내용이 있던 글이 갑자기 빈 내용으로 저장되려 할 때
                // 이것이 의도된 삭제인지, 오류인지 판단하기 어려우므로 일단 로그만 남기거나
                // 저장을 보류할 수 있다. (사용자가 모든 블록을 지운 경우도 있으므로 조심스럽게 접근)
                console.warn("Attempting to auto-save empty content over an existing post. Skipping.");
                this.alreadySaving = false;
                return; // 저장을 건너뛴다.
            }

            if (this.data.get("post-type") === "form") {
                this.editorDataTarget.value = JSON.stringify(outputData);
            } else {
                this.editorDataTarget.value = JSON.stringify(outputData);
                let params = {
                    commit: "auto_save"
                };
                params[this.data.get("param-key")] = {
                    title: this.titleTarget.value,
                    subtitle: this.subtitleTarget.value,
                    tag_list: this.tagListTarget.value,
                    post_category_slug: this.hasPostCategorySlugTarget && this.postCategorySlugTarget.value, // TODO template_draft 자동저장이랑 공유하는 로직. 수정필요.
                    editor_data: JSON.stringify(outputData)
                }

                api.patch(this.data.get("update-url"), params)
                    .then((res) => {
                        // res나 res.data가 undefined여도 오류가 발생하지 않도록 ?. 사용
                        const succeedMessage = res?.data?.succeed_message;
                        const warningMessage = res?.data?.warning_message;

                        // TODO template_draft 자동저장이랑 공유하는 로직. 수정필요.
                        if (this.hasAutoSavedLabelTarget) {
                            if (succeedMessage && succeedMessage.trim() !== "") {
                                this.autoSavedLabelTarget.classList.remove("hidden");
                                this.autoSavedLabelTarget.innerHTML = `${new Date().toLocaleTimeString()}<br>${succeedMessage}`;
                            } else {
                                this.autoSavedLabelTarget.classList.add("hidden");
                            }
                        }

                        if (this.hasWarningLabelTarget) {
                            if (warningMessage && warningMessage.trim() !== "") {
                                this.warningLabelTarget.classList.remove("hidden");
                                this.warningLabelTarget.innerHTML = warningMessage;
                            } else {
                                this.warningLabelTarget.classList.add("hidden");
                            }
                        }

                        if (res?.data?.abandoned_tab) {
                            this.disableEditor(warningMessage); // 에디터 비활성화
                        } else {
                            this.enableEditor(); // 에디터 활성화
                        }

                        if (this.previewWindow && !this.previewWindow.closed) {
                            try {
                                // 자식 창에 정의된 함수를 직접 호출합니다.
                                this.previewWindow.refreshPreview();
                            } catch (e) {
                                // 자식 창이 다른 도메인이거나, 아직 로딩이 덜 되어 함수가 없을 때 발생할 수 있는 오류를 방지합니다.
                                console.error("미리보기 창과 통신할 수 없습니다.", e);
                                // 이 경우, 참조를 초기화하여 다음번 클릭 시 새로 열도록 할 수 있습니다.
                                this.previewWindow = null;
                            }
                        }

                    })
                    .catch((error) => {
                        // error.response가 없는 경우(네트워크 오류 등)를 대비해 ?. 사용
                        const errorMessage = error?.response?.data?.error || "알 수 없는 오류가 발생했습니다.";
                        alert(errorMessage);
                    });
            }

            this.alreadySaving = false;
        }).catch((e) => {
            console.log(e);
            this.alreadySaving = false;
        })
    }

    clearAndRenderDraft(editorData) {
        this.editor.blocks.clear();
        this.editor.blocks.render(editorData).then(() => {
            this.autoSave();
        });
    }

    clearAndRender(editorData) {
        this.editor.save().then((outputData) => {
            this.editor.blocks.clear();
            this.editor.blocks.render(editorData).then(() => {
                this.autoSave();
            });
        });
    }

    // block = {id:, data: { items: [..], style: "unordered"  }, type:}
    legacyListToNestedList = (block) => {
        let blockData = block.data
        blockData.items = blockData.items.map((item) => {
            if (typeof(item) === "string") {
                return { content: item, items: [] };
            } else {
                return item;
            }
        })
        block.data = blockData;
        return block;
    }

    async connect() {
        let editorJsController = this;
        let defaultData = {};

        try {
            if (this.data.get("post-type") === "form") {
                defaultData = JSON.parse(`${this.data.get("default-data")}`);
            } else {
                let response = await api.get(this.data.get("fetch-url"))
                const {
                    title,
                    subtitle,
                    editor_data,
                    tag_list,
                    post_category_slug
                } = response.data;

                this.titleTarget.value = title;
                this.subtitleTarget.value = subtitle;
                this.tagListTarget.value = tag_list;
                if (post_category_slug && post_category_slug.length > 0) {
                    this.postCategorySlugTarget.value = post_category_slug;
                }
                defaultData = editor_data;
            }
        } catch (error) {
            alert("콘텐츠를 불러오는 데 실패했습니다. 잠시 후 페이지를 새로고침 해주세요. 문제가 계속되면 고객센터로 문의해주세요.");
            // 에디터 초기화 로직을 아예 실행하지 않도록 여기서 함수를 종료
            return;
        }


        // block format 업데이트로 인한 data migration 코드들
        // block = {id:, data:, type:}
        defaultData.blocks = defaultData?.blocks?.map((block) => {
            switch (block.type) {
                case "list":
                    return this.legacyListToNestedList(block)
                default:
                    return block;
            }
        }) || []

        const imageToolConfig = createImageUploaderConfig(this);
        const defaultBlockTunes = ['duplicate'];

        const getCommonTools = (options = {}) => {
            const { includeColumns = false, columnsTool = null } = options;
            
            const tools = {
                blockStyle: BlockStyleTune,
                textStyle: TextStyleTune,
                duplicate: DuplicateTune,
                paragraph: {
                    class: Paragraph,
                    tunes: ['textStyle', ...defaultBlockTunes],
                    inlineToolbar: true,
                    config: {
                        preserveBlank: true,
                    }
                },
                header: {
                    class: Header,
                    tunes: ['textStyle', ...defaultBlockTunes],
                    inlineToolbar: true,
                    shortcut: 'CMD+SHIFT+H',
                    config: {
                        levels: [2, 3, 4],
                        defaultLevel: 2,
                        placeholder: '헤더 내용을 입력하세요',
                        // blockTuneDisplayDefault: true,
                    },
                },
            };
            
            // columns를 3번째 위치에 조건부 삽입
            if (includeColumns && columnsTool) {
                tools.columns = columnsTool;
            }
            
            // 나머지 툴들 추가
            Object.assign(tools, {
                linkTool: {
                    class: LinkTool,
                    shortcut: 'CMD+L',
                    config: {
                        endpoint: '/api/links/parse'
                    }
                },
                image: {
                    class: GalleryTool,
                    shortcut: 'CMD+SHIFT+I',
                    config: imageToolConfig
                },
                list: {
                    class: EditorjsList,
                    shortcut: 'CMD+SHIFT+L',
                    inlineToolbar: true,
                    config: {
                        // blockTuneDisplayDefault: true,
                    }
                },
                quote: {
                    class: Quote,
                    inlineToolbar: true,
                    shortcut: 'CMD+SHIFT+O',
                    config: {
                        quotePlaceholder: '내용을 입력하세요',
                        captionPlaceholder: '\t'
                    },
                },
                code: {
                    class: CodeTool,
                    shortcut: 'CMD+SHIFT+B'
                },
                delimiter: {
                    class: Delimiter,
                    shortcut: 'CMD+SHIFT+D',
                },
                table: {
                    class: Table,
                    shortcut: 'CMD+SHIFT+E',
                    inlineToolbar: true,
                    config: {
                        rows: 2,
                        cols: 2,
                    },
                },
                AnyButton: { // 레거시 이름 그대로 유지..
                    class: Button,
                    inlineToolbar: false,
                    shortcut: 'CMD+SHIFT+A',
                    config: {
                        css: {
                            "btnColor": "editor-button",
                        },
                        defaultAlignment: "center",
                        colorSettingLink: editorJsController.data.get("space-url") !== "demo" && `/${editorJsController.data.get("space-url")}/o/settings/home#primary_colors`,
                        defaultTextColor: editorJsController.primaryTextColorValue, // "#000000",
                        defaultBackgroundColor: editorJsController.primaryBackgroundColorValue, // "#FFFFFF",
                    }
                },
                strikethrough: {
                    class: Strikethrough,
                    shortcut: 'CMD+SHIFT+X',
                },
                underline: {
                    class: Underline,
                    inlineToolbar: true,
                    shortcut: "CMD+U"
                },
                ColorPicker: TextColor,
                Marker: {
                    class: Marker,
                    shortcut: 'CMD+M',
                },
                embed: {
                    class: Embed,
                    config: {
                        services: {
                            youtube: true
                        }
                    }
                },
            });
            
            return tools;
        };

        const columnInternalTools = {
            ...getCommonTools(), // columns 없이 기본 툴들만
            image: {
                class: SimpleImageTool, // Columns 내에서는 SimpleImageTool을 사용합니다.
                shortcut: 'CMD+SHIFT+I',
                config: imageToolConfig // 공통 config를 재사용합니다.
            },
        };

        const columnsTool = {
            class: Columns,
            shortcut: 'CMD+SHIFT+C',
            tunes: ['blockStyle', ...defaultBlockTunes],
            config: {
                EditorJsLibrary: EditorJS,
                tools: columnInternalTools,
                tunes: defaultBlockTunes,
                // ✨ [핵심 연동] undo 인스턴스와 통신하는 콜백 전달
                registerUndoManager: (blockId, colIndex, editorInstance) => {
                    if (this.undo) {
                        this.undo.registerNestedEditor(blockId, colIndex, editorInstance);
                    }
                },
                unregisterUndoManager: (blockId) => {
                    if (this.undo) {
                        this.undo.unregisterNestedEditors(blockId);
                    }
                },

                updateMainEditorState() {
                    console.log("registerChange from Column Editor");
                    if (this.undo) {
                        this.undo.registerChange();
                    }
                }
            }
        };

        const tools = this.lightModeValue ? {
            paragraph: {
                class: Paragraph,
                tunes: [],
                inlineToolbar: true,
                config: {
                    preserveBlank: true,
                }
            },
        } : getCommonTools({ includeColumns: true, columnsTool });

        const holder = this.data.get("editor-container") || "editorContainer"

        this.editor = new EditorJS({
            onReady: () => {
                // onReady가 호출될 때 에디터와 DOM이 여전히 유효한지 확인
                if (!this.editor) {
                    return;
                }

                const holderElement = document.getElementById(holder);

                // DOM 요소가 존재하고 실제로 문서에 연결되어 있는지 확인
                if (!holderElement || !document.body.contains(holderElement)) {
                    return;
                }

                const editor = this.editor;

                this.undo = new UnifiedUndo({ editor });
                this.undo.initialize(defaultData);

                // DragDrop 초기화 시 에러 방지
                try {
                    new DragDrop(editor);
                } catch (error) {
                    // DragDrop 초기화 실패 시 무시 (DOM이 사라진 경우)
                }

                // 드래그 상태를 리셋하는 함수
                const resetDragState = () => {
                    clearTimeout(this.dragResetTimer);
                    this.dragResetTimer = setTimeout(() => {
                        this.isDragging = false;
                    }, 500);
                };

                holderElement.addEventListener('dragstart', () => {
                    this.isDragging = true;
                    // 안전장치: 5초 후 자동으로 드래그 상태 해제
                    clearTimeout(this.dragResetTimer);
                    this.dragResetTimer = setTimeout(() => {
                        this.isDragging = false;
                    }, 5000);
                });

                holderElement.addEventListener('drop', resetDragState);
                holderElement.addEventListener('dragend', resetDragState);

                // 드래그가 에디터 영역을 벗어난 경우도 처리
                holderElement.addEventListener('dragleave', (e) => {
                    // 에디터 영역을 완전히 벗어난 경우에만 처리
                    if (e.target === holderElement && !holderElement.contains(e.relatedTarget)) {
                        resetDragState();
                    }
                });

                // 키보드 입력 시 드래그 상태 강제 해제 및 타이핑 상태 관리
                holderElement.addEventListener('keydown', (e) => {
                    if (this.isDragging) {
                        this.isDragging = false;
                        clearTimeout(this.dragResetTimer);
                    }

                    // Enter, Delete, Backspace 키 입력 시 타이핑 상태로 설정
                    if (e.key === 'Enter' || e.key === 'Delete' || e.key === 'Backspace') {
                        this.isTyping = true;
                        clearTimeout(this.typingTimer);
                        this.typingTimer = setTimeout(() => {
                            this.isTyping = false;
                        }, 500); // 1초 후 타이핑 상태 해제
                    }
                });

                holderElement.addEventListener('click', () => {
                    this.sendCurrentBlockIdToPreview();
                });

                holderElement.addEventListener('keyup', (e) => {
                    // 키보드 방향키로 블록 이동 시에도 동기화
                    if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown'].includes(e.key)) {
                        // 약간의 딜레이를 주어 블록 포커스가 완전히 이동한 후 실행
                        setTimeout(() => {
                            this.sendCurrentBlockIdToPreview();
                        }, 100);
                    }
                });

                if (this.hasPreviewButtonTarget) {
                    this.previewButtonTarget.disabled = false;
                }
            },
            logLevel: process.env.NODE_ENV === 'production' ? 'ERROR' : 'ERROR',
            data: defaultData,
            holder: holder,
            placeholder: "'/'를 눌러 블록을 선택하거나, 원하는 내용을 입력하세요.",
            i18n: korean,
            tools: tools,
            tunes: defaultBlockTunes
        })

        let autoSavePeriod;

        if (this.data.get("post-type") === "form") {
            // TODO 뉴스레터 설정에서 메일러랑, 뉴스레터 하단부분 blob 생성
            // TODO 거기에서 editor_js 가 2개 붙어 있어서 양쪽 모두의 save 이벤트를 받아서 blob 을 input 에 집어넣고나서 form 제출이 되야하는데 일단 간단하게 처리
            autoSavePeriod = 1000;
        } else {
            autoSavePeriod = 15000;
        }

        this.autoSaver = setInterval(() => {
            if (document.hidden === true) return;
            this.autoSave();
        }, autoSavePeriod);

        // if (this.data.get("post-type") === "form") {
        //   this.autoSaver = setInterval(() => {
        //     if(document.hidden === true) return;
        //     this.autoSave();
        //   }, 1000);
        // }

        document.addEventListener("keydown", this.handleKeyDown);

        // IME composition 이벤트 리스너 추가
        document.addEventListener('compositionstart', this.handleCompositionStart);
        document.addEventListener('compositionend', this.handleCompositionEnd);
    }

    // ✨ 키보드 이벤트를 처리하는 통합 핸들러
    handleKeyDown = (e) => {
        const isCmdOrCtrl = window.navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey;

        if (isCmdOrCtrl) {
            switch (e.key.toLowerCase()) {
                case 's': // 저장 (Cmd/Ctrl + S)
                    e.preventDefault();
                    this.autoSave();
                    break;
            }
        }

        // Safari에서만 Shift+Enter 처리 (IME 입력 중이 아닐 때)
        if (this.isSafari && e.shiftKey && e.key === 'Enter' && !this.isComposing) {
            // 현재 포커스된 요소가 paragraph 블록인지 확인
            const activeElement = document.activeElement;
            if (activeElement && activeElement.closest('.ce-paragraph')) {
                e.preventDefault();

                // 대신 일반 Enter 키 이벤트를 발생시킴
                const enterEvent = new KeyboardEvent('keydown', {
                    key: 'Enter',
                    code: 'Enter',
                    keyCode: 13,
                    which: 13,
                    bubbles: true,
                    cancelable: true
                });
                activeElement.dispatchEvent(enterEvent);
            }
        }
    };

    // IME composition 이벤트 핸들러 (arrow function으로 자동 바인딩)
    handleCompositionStart = () => {
        this.isComposing = true;
    }

    handleCompositionEnd = () => {
        this.isComposing = false;
    }

    disconnect() {
        // 컨트롤러가 페이지에서 사라질 때, 열려있던 팝업창도 함께 닫아줍니다. (선택사항이지만 좋은 UX)
        if (this.previewWindow && !this.previewWindow.closed) {
            this.previewWindow.close();
        }

        window.connectPreview = null;

        if (this.autoSaver) {
            clearInterval(this.autoSaver);
        }

        if (this.dragResetTimer) {
            clearTimeout(this.dragResetTimer);
        }

        if (this.typingTimer) {
            clearTimeout(this.typingTimer);
        }

        document.removeEventListener("keydown", this.handleKeyDown); // ✨ 통합 핸들러 제거

        // composition 이벤트 리스너 제거
        document.removeEventListener('compositionstart', this.handleCompositionStart);
        document.removeEventListener('compositionend', this.handleCompositionEnd);

        if (this.editor) {
            this.editor.isReady.then(() => {
                this.editor.destroy();
                this.editor = null; // 참조 제거
            });
        }
    }
}